import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { queryKey } from 'constants/queryKey';
import type {
  OriginalImageData,
  TranslationFields,
} from 'types/AiHubTranslation';

type RevertAllParams = {
  fields: TranslationFields;
  appliedFields: {
    postText: boolean;
    firstComment: boolean;
    images: boolean;
  };
};

export const useRevertTranslation = (
  postId: number,
  options?: { onReverted?: () => void | Promise<void> },
) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidatePostDetail = useInvalidatePostDetailQueries();
  const invalidatePosts = useInvalidatePostsQueries();

  const onRevertSuccess = async () => {
    await queryClient.refetchQueries(queryKey.postDetail(postId));
    queryClient.invalidateQueries(queryKey.postTranslation(postId));
    invalidatePostDetail({ postId });
    invalidatePosts();
    await options?.onReverted?.();
    toast(
      t('translation.revertSuccess', {
        defaultValue: 'Reverted to original',
      }),
      'success',
    );
  };

  const onRevertError = (e: ApiClientError) => {
    invalidatePostDetail({ postId });
    toast(
      e?.userMessage ||
        t('translation.revertFailed', {
          defaultValue: 'Failed to revert translation',
        }),
      'error',
    );
  };

  const revertPostText = useMutation(
    (originalText: string) =>
      PostApi.savePartial({
        postId,
        values: { text: originalText },
      }),
    {
      onSuccess: onRevertSuccess,
      onError: onRevertError,
    },
  );

  const revertFirstComment = useMutation(
    async (originalComment: string) => {
      const post = await PostApi.detail(postId);
      return PostApi.savePartial({
        postId,
        values: {
          attachments: {
            ...post.attachments,
            firstComment: {
              text: originalComment,
              status: post.attachments?.firstComment?.status ?? null,
            },
          },
        },
      });
    },
    {
      onSuccess: onRevertSuccess,
      onError: onRevertError,
    },
  );

  const revertImages = useMutation(
    async (originalImages: OriginalImageData[]) => {
      const post = await PostApi.detail(postId);
      const cards = [...(post.attachments?.cards ?? [])];

      for (const img of originalImages) {
        const card = cards[img.cardIndex];
        if (card?.medium && card.medium.type === 'image') {
          card.medium = {
            ...card.medium,
            id: img.mediumId,
          };
        }
      }

      return PostApi.savePartial({
        postId,
        values: { attachments: { ...post.attachments, cards } },
      });
    },
    {
      onSuccess: onRevertSuccess,
      onError: onRevertError,
    },
  );

  const revertAll = useMutation(
    async ({ fields, appliedFields }: RevertAllParams) => {
      const post = await PostApi.detail(postId);
      const updates: Record<string, unknown> = {};

      if (appliedFields.postText && fields.postText?.original) {
        updates.text = fields.postText.original;
      }

      const attachments = { ...post.attachments };
      let attachmentsModified = false;

      if (appliedFields.firstComment && fields.firstComment?.original) {
        attachments.firstComment = {
          text: fields.firstComment.original,
          status: attachments.firstComment?.status ?? null,
        };
        attachmentsModified = true;
      }

      if (appliedFields.images && fields.images && fields.images.length > 0) {
        const cards = [...(attachments.cards ?? [])];
        for (let i = 0; i < fields.images.length; i++) {
          const img = fields.images[i];
          const cardIndex = img.cardIndex ?? i;
          const card = cards[cardIndex];
          if (
            card?.medium &&
            card.medium.type === 'image' &&
            img.mediumId != null
          ) {
            card.medium = {
              ...card.medium,
              id: img.mediumId,
            };
          }
        }
        attachments.cards = cards;
        attachmentsModified = true;
      }

      if (attachmentsModified) {
        updates.attachments = attachments;
      }

      return PostApi.savePartial({ postId, values: updates });
    },
    {
      onSuccess: onRevertSuccess,
      onError: onRevertError,
    },
  );

  return { revertPostText, revertFirstComment, revertImages, revertAll };
};
